import React, { ChangeEvent, FC, Fragment, KeyboardEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldArray, FieldArrayRenderProps, getIn, useFormikContext } from 'formik';
import {
  Button,
  Unstable_Grid2 as Grid,
  IconButton,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { ClientUrlParamsUpdateInput } from 'api';
import { DeleteIcon } from 'components';
import { useNotify } from 'hooks';

interface Props {
  index: number;
}

const styles = {
  tableHeadRow: {
    border: 0,
  },
  tableBodyRow: {
    height: 8,
    '&:nth-of-type(even)': {
      background: 'common.white',
    },
  },
} as const;

const ValueOptions: FC<Props> = ({ index }) => {
  const formik = useFormikContext<ClientUrlParamsUpdateInput>();
  const [newOptionValue, setNewOptionValue] = useState('');
  const [validationError, setValidationError] = useState('');
  const { t } = useTranslation();
  const notify = useNotify();

  const { values, setFieldValue } = formik;
  const config = values.configuration[index];
  const valueOptionsField = `configuration[${index}].value_options`;
  const defaultOptionField = `configuration[${index}].default_option_value`;

  const valueOptions = getIn(formik.values, valueOptionsField);
  const defaultValue = getIn(formik.values, defaultOptionField);

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const { value } = event.currentTarget;
    setNewOptionValue(value.trim());
  }

  function handleKeyDown(
    event: KeyboardEvent<HTMLDivElement>,
    arrayHelpers: FieldArrayRenderProps
  ) {
    if (event.keyCode !== 13) {
      return;
    }
    handleAddValueOption(arrayHelpers);
  }

  function handleAddValueOption(arrayHelpers: FieldArrayRenderProps) {
    if (!newOptionValue) {
      setValidationError(t('parameters:trackingOptionsRequired'));
      return notify.error({ message: t('parameters:trackingOptionsRequired') });
    }

    if (valueOptions.includes(newOptionValue)) {
      setValidationError(t('parameters:trackingOptionsExists'));
      return notify.error({ message: t('parameters:trackingOptionsExists') });
    }
    arrayHelpers.push(newOptionValue);
    if (!values.configuration[index].default_option_value) {
      setFieldValue(defaultOptionField, newOptionValue);
    }
    setNewOptionValue('');
  }

  function handleRemoveOption(optionIndex: number, arrayHelpers: FieldArrayRenderProps) {
    const option = valueOptions[optionIndex];

    arrayHelpers.remove(optionIndex);

    if (defaultValue === option) {
      const nextDefault = valueOptions[optionIndex + 1] || valueOptions[0] || null;
      setFieldValue(defaultOptionField, nextDefault);
    }
  }

  function renderOption(option: string, optionIndex: number, arrayHelpers: FieldArrayRenderProps) {
    return (
      <TableRow key={optionIndex} sx={styles.tableBodyRow}>
        <TableCell align="left" padding="none">
          <Radio
            color="primary"
            checked={config.default_option_value === option}
            value={option}
            onClick={() => setFieldValue(defaultOptionField, option)}
          />
        </TableCell>
        <TableCell padding="none" style={{ width: '70%' }}>
          <TextField variant="standard" fullWidth disabled value={option} />
        </TableCell>
        <TableCell padding="none" style={{ width: '10%' }}>
          <IconButton onClick={() => handleRemoveOption(optionIndex, arrayHelpers)} size="large">
            <DeleteIcon />
          </IconButton>
        </TableCell>
      </TableRow>
    );
  }

  function renderTable(arrayHelpers: FieldArrayRenderProps) {
    if (!valueOptions || !valueOptions.length) {
      return null;
    }

    return (
      <Table>
        <TableHead>
          <TableRow sx={{ head: styles.tableHeadRow }}>
            <TableCell align="left" padding="none">
              <Typography variant="subtitle2">{t('common:default')}</Typography>
            </TableCell>
            <TableCell padding="none" />
            <TableCell padding="none" />
          </TableRow>
        </TableHead>
        <TableBody>
          {config.value_options.map((option, optionIndex) =>
            renderOption(option, optionIndex, arrayHelpers)
          )}
        </TableBody>
      </Table>
    );
  }

  return (
    <FieldArray name={valueOptionsField}>
      {arrayHelpers => (
        <Fragment>
          <Grid container alignItems="center" spacing={2}>
            <Grid xs>
              <TextField
                variant="outlined"
                margin="normal"
                placeholder={t('parameters:trackingOptionsNew')}
                fullWidth
                InputProps={{ disableUnderline: !validationError }}
                onChange={handleChange}
                value={newOptionValue}
                onKeyDown={event => handleKeyDown(event, arrayHelpers)}
                error={!!validationError}
                InputLabelProps={{ shrink: !!validationError }}
                helperText={validationError}
              />
            </Grid>
            <Grid>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleAddValueOption(arrayHelpers)}
              >
                {t('common:add')}
              </Button>
            </Grid>
          </Grid>
          {renderTable(arrayHelpers)}
        </Fragment>
      )}
    </FieldArray>
  );
};

export default ValueOptions;
